import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Popover } from 'reactstrap'

import DatePicker from '../../common/form/DatePicker'
import FilterCheckbox from './FilterCheckbox'
import CollapsibleFilterSection from './CollapsibleFilterSection'
import ProgramsEventsModal from './ProgramsEventsModal'

import { GENDER_SELECT_OPTIONS } from '../../../misc/Constants'
import { getCommunityCategories } from '../../../actions/programCategoryActions'
import { pluralize } from '../../../utility/helperFunctions'

import classes from './FiltersPopover.module.scss'

const ENROLLMENT_STATUSES = ['active', 'future', 'waitlist', 'inactive']
const LEVELS = ['1', '2', '3', '4', '5', '6', '6+']

const AGE_OPTIONS = Array.from({ length: 118 }, (_, i) => i + 3)

const FiltersPopover = ({
  isOpen,
  toggle,
  currentFilters,
  initialFilters,
  onApply,
}) => {
  const dispatch = useDispatch()
  const categories = useSelector(state => state.dashboard.communityCategories)

  const [staged, setStaged] = useState(currentFilters)
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false)
  const [selectedPrograms, setSelectedPrograms] = useState([])
  const [selectedEvents, setSelectedEvents] = useState([])

  useEffect(() => {
    if (isOpen) {
      dispatch(getCommunityCategories())
    }
  }, [isOpen, dispatch])

  const toggleArrayValue = (key, value) => {
    setStaged(prev => {
      const arr = prev[key]
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      return { ...prev, [key]: next }
    })
  }

  const handleClearAll = () => {
    setStaged(initialFilters)
    setSelectedPrograms([])
    setSelectedEvents([])
    onApply(initialFilters)
    toggle()
  }

  const pendingFilters = {
    ...staged,
    courseIds: selectedPrograms.map(p => p.id),
    eventIds: selectedEvents.map(e => e.id),
  }

  const hasPendingChanges = JSON.stringify(pendingFilters) !== JSON.stringify(currentFilters)

  const handleApply = () => {
    onApply(pendingFilters)
    toggle()
  }

  const handleProgramsEventsConfirm = (programs, events) => {
    setSelectedPrograms(programs)
    setSelectedEvents(events)
  }

  const isAllLevels = staged.levels.length === 0

  const handleAllLevels = () => {
    setStaged(prev => ({ ...prev, levels: [] }))
  }

  const programsEventsLabel = () => {
    const parts = [
      { count: selectedPrograms.length, label: 'Program' },
      { count: selectedEvents.length, label: 'Event' },
    ]
      .filter(p => p.count > 0)
      .map(p => `${p.count} ${pluralize(p.label, p.count)}`)

    return parts.length > 0 ? parts.join(', ') + ' Selected' : null
  }

  return (
    <>
      <Popover
        placement='bottom-start'
        isOpen={isOpen}
        target='filtersButton'
        toggle={toggle}
        trigger='legacy'
        className={classes.popoverContainer}
      >
        <div className={classes.popover}>
          <div className={classes.scrollContent}>
            <div className={classes.header}>
              <h6 className={classes.title}>Filters</h6>
              <button className={classes.clearAllButton} onClick={handleClearAll}>Clear all</button>
            </div>
            <hr className={classes.sectionDivider} />

            <CollapsibleFilterSection title='Enrollment Status'>
              <div className={classes.checkboxGroup}>
                {ENROLLMENT_STATUSES.map(status => (
                  <FilterCheckbox
                    key={status}
                    id={`filter-enrollment-${status}`}
                    isSelected={staged.enrollmentStatuses.includes(status)}
                    onClick={() => toggleArrayValue('enrollmentStatuses', status)}
                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                  />
                ))}
              </div>
            </CollapsibleFilterSection>

            <CollapsibleFilterSection title='Gender'>
              <div className={classes.checkboxGroup}>
                {GENDER_SELECT_OPTIONS.map(gender => (
                  <FilterCheckbox
                    key={gender.value}
                    id={`filter-gender-${gender.value}`}
                    isSelected={staged.genders.includes(gender.value)}
                    onClick={() => toggleArrayValue('genders', gender.value)}
                    label={gender.label}
                  />
                ))}
              </div>
            </CollapsibleFilterSection>

            <CollapsibleFilterSection title='Categories'>
              <div className={classes.checkboxGroup}>
                {categories?.map(category => (
                  <FilterCheckbox
                    key={category.id}
                    id={`filter-category-${category.id}`}
                    isSelected={staged.categoryIds.includes(category.id)}
                    onClick={() => toggleArrayValue('categoryIds', category.id)}
                    label={category.name}
                  />
                ))}
              </div>
            </CollapsibleFilterSection>

            <CollapsibleFilterSection title='Programs & Events'>
              <button
                className={classes.programsEventsButton}
                onClick={() => setIsProgramsModalOpen(true)}
              >
                {programsEventsLabel()
                  ? <span className={classes.programsEventsSelected}>{programsEventsLabel()}</span>
                  : 'Choose Programs or Events from Library'
                }
              </button>
            </CollapsibleFilterSection>

            <CollapsibleFilterSection title='Date Joined'>
              <div className={classes.dateRow}>
                <div className={classes.dateField}>
                  <span className={classes.fieldLabel}>Start Date</span>
                  <DatePicker
                    selected={staged.dateJoinedFrom ? new Date(staged.dateJoinedFrom) : null}
                    onChange={(date) => setStaged(prev => ({ ...prev, dateJoinedFrom: date ? date.toISOString().split('T')[0] : null }))}
                    dateFormat='MM/dd/yyyy'
                    placeholderText='Select date'
                    isClearable
                  />
                </div>
                <div className={classes.dateField}>
                  <span className={classes.fieldLabel}>End Date</span>
                  <DatePicker
                    selected={staged.dateJoinedTo ? new Date(staged.dateJoinedTo) : null}
                    onChange={(date) => setStaged(prev => ({ ...prev, dateJoinedTo: date ? date.toISOString().split('T')[0] : null }))}
                    dateFormat='MM/dd/yyyy'
                    placeholderText='Select date'
                    isClearable
                  />
                </div>
              </div>
            </CollapsibleFilterSection>

            <CollapsibleFilterSection title='Age Range'>
              <div className={classes.dateRow}>
                <div className={classes.dateField}>
                  <label htmlFor='filter-age-from' className={classes.fieldLabel}>Lowest</label>
                  <select
                    id='filter-age-from'
                    className={classes.selectInput}
                    value={staged.ageFrom ?? ''}
                    onChange={(e) => setStaged(prev => ({ ...prev, ageFrom: e.target.value ? Number(e.target.value) : null }))}
                  >
                    <option value=''>Any</option>
                    {AGE_OPTIONS.map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                </div>
                <div className={classes.dateField}>
                  <label htmlFor='filter-age-to' className={classes.fieldLabel}>Highest</label>
                  <select
                    id='filter-age-to'
                    className={classes.selectInput}
                    value={staged.ageTo ?? ''}
                    onChange={(e) => setStaged(prev => ({ ...prev, ageTo: e.target.value ? Number(e.target.value) : null }))}
                  >
                    <option value=''>Any</option>
                    {AGE_OPTIONS.map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CollapsibleFilterSection>

            <CollapsibleFilterSection title='Levels'>
              <div className={classes.levelsGrid}>
                {LEVELS.map(level => (
                  <FilterCheckbox
                    key={level}
                    id={`filter-level-${level}`}
                    isSelected={staged.levels.includes(level)}
                    onClick={() => toggleArrayValue('levels', level)}
                    label={`L${level}`}
                  />
                ))}
                <FilterCheckbox
                  id='filter-level-all'
                  isSelected={isAllLevels}
                  onClick={handleAllLevels}
                  label='All Levels'
                />
              </div>
            </CollapsibleFilterSection>
          </div>

          <div className={classes.applyFooter}>
            {hasPendingChanges && <span className={classes.pendingText}>Pending filters</span>}
            <button className={classes.applyButton} onClick={handleApply} disabled={!hasPendingChanges}>Apply</button>
          </div>
        </div>
      </Popover>

      {isProgramsModalOpen && (
        <ProgramsEventsModal
          isOpen={isProgramsModalOpen}
          onClose={() => { setIsProgramsModalOpen(false); toggle() }}
          initialPrograms={selectedPrograms}
          initialEvents={selectedEvents}
          onConfirm={handleProgramsEventsConfirm}
        />
      )}
    </>
  )
}

FiltersPopover.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  currentFilters: PropTypes.object.isRequired,
  initialFilters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
}

export default FiltersPopover
